import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Briefcase, Calendar } from 'lucide-react';

export const Experience = () => {
  const experiences = [
    {
      company: 'Cigniti Technologies (Coforge)',
      role: 'Senior Engineer',
      duration: 'May 2023 - Present',
      location: 'Hyderabad',
      projects: [
        {
          name: 'Business Accelerator 365',
          description: 'Risk management and compliance application for airports in Australia and New Zealand.',
          achievements: [
            'Led development of External User Registration and Hazard Management features',
            'Implemented Azure Active Directory integration and Power BI dashboards',
            'Managed complete development lifecycle from requirements to deployment'
          ],
          tech: ['.NET Core', 'SQL Server', 'Power BI', 'Azure AD', 'Azure Functions']
        },
        {
          name: 'MAF - Controls Self-Assessment',
          description: 'Enterprise controls assessment system for Majid Al Futtaim.',
          achievements: [
            'Developed Angular-based frontend with .NET Core backend',
            'Implemented Azure CI/CD pipeline using YAML',
            'Integrated Docker containerization for deployment'
          ],
          tech: ['.NET Core', 'Angular', 'Azure DevOps', 'Docker', 'YAML']
        }
      ]
    },
    {
      company: 'Wincom Technologies (Takaful Emarat)',
      role: 'Senior Software Developer',
      duration: 'April 2022 - May 2023',
      location: 'Dubai (Remote)',
      projects: [
        {
          name: 'EnhancedMedical API',
          description: 'Comprehensive insurance API handling quotations, policies, and claims.',
          achievements: [
            'Developed Ecard and Credit Note features',
            'Led migration of portals across Azure regions',
            'Integrated Kofax for document processing'
          ],
          tech: ['.NET Core', 'SQL Server', 'Kofax', 'Azure', 'Entity Framework']
        }
      ]
    },
    {
      company: 'Straive (SPI Global)',
      role: 'Senior Software Developer',
      duration: 'October 2021 - March 2022',
      location: 'Hyderabad',
      projects: [
        {
          name: 'LexisNexis Source Management',
          description: 'Data management system for LexisNexis using Blazor and MySQL.',
          achievements: [
            'Led team from project inception to production deployment',
            'Designed and implemented Blazor-based UI architecture',
            'Established Azure CI/CD pipeline for automated deployments'
          ],
          tech: ['Blazor', '.NET Core', 'MySQL', 'Azure CI/CD', 'Bootstrap']
        }
      ]
    },
    {
      company: 'ZENQ',
      role: 'Senior Software Developer',
      duration: 'October 2019 - October 2021',
      location: 'Hyderabad',
      projects: [
        {
          name: 'Optis LeaveExpert',
          description: 'Cloud software for leave management and regulatory compliance.',
          achievements: [
            'Developed features using .NET Core, MSBI, and Angular 9',
            'Created Azure build pipeline for CI/CD',
            'Implemented SSIS/SSRS reporting solutions'
          ],
          tech: ['.NET Core', 'Angular 9', 'SSIS', 'SSRS', 'Azure', 'SQL Server']
        },
        {
          name: 'zClient Portal',
          description: 'Internal portal for client, project, and timesheet management.',
          achievements: [
            'Built from scratch using .NET Core Blazor',
            'Designed comprehensive project and invoice management system'
          ],
          tech: ['Blazor', '.NET Core', 'Web API', 'SQL Server', 'Bootstrap']
        }
      ]
    },
    {
      company: 'Nisum Technologies',
      role: 'Software Engineer',
      duration: 'May 2017 - October 2019',
      location: 'Hyderabad',
      projects: [
        {
          name: 'GAP Foundation',
          description: 'Employee volunteer service with donation matching programs.',
          achievements: [
            'Developed new features with .NET Core and Angular 6',
            'Implemented Azure Search Services integration'
          ],
          tech: ['.NET Core', 'DNN', 'WCF', 'Angular 6', 'Azure Search']
        }
      ]
    }
  ];

  return (
    <section id="experience" className="py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Work <span className="text-gradient-azure">Experience</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-azure mx-auto rounded-full"></div>
        </div>

        {/* Timeline */}
        <div className="max-w-5xl mx-auto space-y-8">
          {experiences.map((exp, index) => (
            <div
              key={index}
              className="relative pl-8 sm:pl-12 pb-8 border-l-2 border-primary/30 last:border-l-0 last:pb-0 animate-fade-in-up"
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              {/* Timeline Dot */}
              <div className="absolute left-0 top-0 -translate-x-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background"></div>

              {/* Company Card */}
              <Card className="glass-card border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-xl sm:text-2xl mb-2 flex items-center">
                        <Briefcase className="w-5 h-5 mr-2 text-primary" />
                        {exp.company}
                      </CardTitle>
                      <CardDescription className="text-base sm:text-lg font-medium text-foreground">
                        {exp.role}
                      </CardDescription>
                    </div>
                    <Badge variant="secondary" className="text-sm px-3 py-1 w-fit">
                      <Calendar className="w-3 h-3 mr-1" />
                      {exp.duration}
                    </Badge>
                  </div>
                </CardHeader>

                {/* Projects */}
                <CardContent className="space-y-6">
                  {exp.projects.map((project, pIndex) => (
                    <div key={pIndex} className="space-y-3">
                      <h4 className="text-lg font-semibold text-primary">{project.name}</h4>
                      <p className="text-sm sm:text-base text-muted-foreground">{project.description}</p>
                      
                      <ul className="space-y-2 text-sm sm:text-base text-muted-foreground">
                        {project.achievements.map((achievement, aIndex) => (
                          <li key={aIndex} className="flex items-start">
                            <span className="text-primary mr-2">•</span>
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="flex flex-wrap gap-2 pt-2">
                        {project.tech.map((tech, tIndex) => (
                          <Badge key={tIndex} variant="outline" className="text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};